/* What is allowed to survive from a rich text field into a document.
 *
 * This is security code, not formatting code. Whatever ends up here is stored
 * on the document and rendered again later -- in the app, and by Frappe's own
 * print format on the server, into a PDF that gets emailed to a customer. A
 * script that gets in runs for everyone who opens it afterwards.
 *
 * The editor before this wrote `innerHTML` straight through: pasting from a
 * web page put whatever that page contained into an invoice's terms.
 *
 * It is an allow-list, and deliberately a short one. Anything not named is
 * removed, which is the only way this stays safe as browsers grow features.
 * Unknown *tags* are unwrapped rather than dropped, so pasting from a word
 * processor keeps the words and loses the markup; unknown *attributes* are
 * simply dropped.
 */

/** Tags that may stay, with the attributes each may keep. */
const ALLOWED: Record<string, readonly string[]> = {
  p: [], br: [], div: [], span: [],
  b: [], strong: [], i: [], em: [], u: [], s: [], strike: [],
  h1: [], h2: [], h3: [], h4: [], h5: [], h6: [],
  ul: [], ol: ['start'], li: [],
  blockquote: [], pre: [], code: [], hr: [],
  a: ['href', 'title'],
  // Terms and conditions are full of little tables.
  table: [], thead: [], tbody: [], tfoot: [], tr: [],
  td: ['colspan', 'rowspan'], th: ['colspan', 'rowspan'],
  img: ['src', 'alt', 'width', 'height'],
}

/** Tags whose *contents* go too. Unwrapping a <script> would run it. */
const DROP_ENTIRELY = new Set([
  'script', 'style', 'iframe', 'object', 'embed', 'applet', 'link', 'meta',
  'base', 'form', 'input', 'button', 'select', 'textarea', 'template',
  'noscript', 'svg', 'math', 'frame', 'frameset',
])

/** Where a link or an image may point.
 *
 *  `javascript:` is the obvious one. `data:` is the less obvious one: a
 *  `data:text/html` or `data:image/svg+xml` URL carries a whole document,
 *  script included, so no `data:` is allowed at all rather than trying to
 *  tell the safe ones apart. */
const SAFE_LINK = /^(https?:|mailto:|tel:|#|\/|$)/i
const SAFE_IMAGE = /^(https?:|\/)/i

function safeUrl(value: string, forImage: boolean): string | null {
  // A URL is compared with its whitespace and control characters removed,
  // because "java\tscript:" is a working javascript: URL in some parsers.
  const cleaned = value.replace(/[\u0000- \u007f- ]/g, '')
  return (forImage ? SAFE_IMAGE : SAFE_LINK).test(cleaned) ? value.trim() : null
}

function clean(node: Node, into: Node, doc: Document): void {
  for (const child of Array.from(node.childNodes)) {
    if (child.nodeType === Node.TEXT_NODE) {
      into.appendChild(doc.createTextNode(child.nodeValue ?? ''))
      continue
    }
    if (child.nodeType !== Node.ELEMENT_NODE) continue      // comments, CDATA

    const element = child as Element
    const tag = element.tagName.toLowerCase()

    if (DROP_ENTIRELY.has(tag)) continue

    const allowedAttributes = ALLOWED[tag]
    if (!allowedAttributes) {
      // Keep the words, lose the wrapper.
      clean(element, into, doc)
      continue
    }

    const kept = doc.createElement(tag)
    for (const attribute of allowedAttributes) {
      const value = element.getAttribute(attribute)
      if (value === null) continue
      if (attribute === 'href' || attribute === 'src') {
        const url = safeUrl(value, attribute === 'src')
        if (url === null) continue
        kept.setAttribute(attribute, url)
      } else {
        kept.setAttribute(attribute, value)
      }
    }
    // A link that opens elsewhere should not be able to reach back.
    if (tag === 'a' && kept.hasAttribute('href')) {
      kept.setAttribute('rel', 'noopener noreferrer')
    }
    clean(element, kept, doc)
    into.appendChild(kept)
  }
}

/** The same HTML, with everything not on the allow-list taken out. */
export function sanitiseHtml(html: string): string {
  if (!html) return ''
  // Parsed as a whole document by the browser's own parser, in a context
  // that does not execute anything: the fragment parser is what turns a
  // half-open tag into something different from what it looks like.
  const doc = new DOMParser().parseFromString(`<body>${html}</body>`, 'text/html')
  const out = doc.createElement('div')
  clean(doc.body, out, doc)
  return out.innerHTML
}

/** Plain text, for pasting into a field that has no formatting at all.
 *
 * Sanitised first. `textContent` on the raw HTML happily returns the *body of
 * a script tag* as if it were prose, so pasting a page with a script into a
 * plain Text field put `alert(1)` into the field as text. Harmless there, and
 * not harmless the moment somebody renders that field as HTML somewhere else.
 */
export function textOf(html: string): string {
  const doc = new DOMParser().parseFromString(`<body>${sanitiseHtml(html)}</body>`, 'text/html')
  return doc.body.textContent ?? ''
}
